import * as React from "react";

import "./index.css";
import {
  Routes,
  Route,
  Link,
  useNavigate,
  useLocation,
  Navigate,
  Outlet,
} from "react-router-dom";
import { Flowbite, useThemeMode } from "flowbite-react";

import { Layout } from "./Layout";
import DashboardPage from "./pages/Dashboard";
import UserProfilePage from "./pages/UserProfile";
import WorkoutPage from "./pages/Workout";
import LoginPage from "./pages/Login";
import { ROUTES } from "./constants/routes";
import WorkoutDetails from "./pages/WorkoutDetails";
import Exercises from "./pages/Exercises";
import Progressions from "./pages/Progressions";
import { useLoginMutation } from "./libs/api";
// import { initFlowbite } from 'flowbite';
/**
 * This represents some generic auth provider API, like Firebase.
 */
// const fakeAuthProvider = {
//   isAuthenticated: false,
//   signin(callback: VoidFunction) {
//     fakeAuthProvider.isAuthenticated = true;
//     setTimeout(callback, 100); // fake async
//   },
//   signout(callback: VoidFunction) {
//     fakeAuthProvider.isAuthenticated = false;
//     setTimeout(callback, 100);
//   },
// };

const flowbiteTheme = {
  theme: {
    tab: {
      tablist: {
        base: "flex text-center",
        styles: {
          default:
            "flex-wrap border-b border-gray-200 dark:border-gray-700",
        },
        tabitem: {
          base: "flex items-center justify-center p-4 rounded-t-lg text-sm font-medium first:ml-0 disabled:cursor-not-allowed disabled:text-gray-400 disabled:dark:text-gray-500 focus:ring-4 focus:ring-cyan-300 focus:outline-none",
          styles: {
            default: {
              base: "rounded-t-lg",
              active: {
                on: "bg-gray-100 text-cyan-600 dark:bg-gray-700 dark:text-white font-semibold",
                off: "text-gray-500 hover:bg-gray-50 hover:text-gray-600 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200",
              },
            },
          },
        },
      },
    },
  },
};

export default function App() {
  const isDarkStored = localStorage.getItem("color-theme") === "dark";
  return (
    <Flowbite theme={{ ...flowbiteTheme, dark: isDarkStored }}>
      <AuthProvider>
        <Routes>
          <Route path={ROUTES.LOGIN} element={<LoginPage />} />
          <Route element={<Layout />}>
            <Route
              path={ROUTES.DASHBOARD}
              element={
                <RequireAuth>
                  <DashboardPage />
                </RequireAuth>
              }
            />
            <Route
              path={ROUTES.USER}
              element={
                <RequireAuth>
                  <UserProfilePage />
                </RequireAuth>
              }
            />
            <Route
              path={ROUTES.WORKOUTS}
              element={
                <RequireAuth>
                  <WorkoutPage />
                </RequireAuth>
              }
            />
            <Route
              path={ROUTES.WORKOUT_DETAILS}
              element={
                <RequireAuth>
                  <WorkoutDetails />
                </RequireAuth>
              }
            />
            <Route
              path={ROUTES.EXERCISES}
              element={
                <RequireAuth>
                  <Exercises />
                </RequireAuth>
              }
            />
            <Route
              path={ROUTES.PROGRESSIONS}
              element={
                <RequireAuth>
                  <Progressions />
                </RequireAuth>
              }
            />
          </Route>
        </Routes>
      </AuthProvider>
    </Flowbite>
  );
}

interface AuthContextType {
  user: any;
  signin: (
    user: {
      phoneNumber: string;
      pin: string;
    },
    callback?: VoidFunction
  ) => void;
  signout: (callback?: VoidFunction) => void;
}

const AuthContext = React.createContext<AuthContextType>(null!);

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<Record<string, string | null> | null>({
    jwt: window.localStorage.getItem("userJwt"),
  });

  const [login, { data, isLoading, error }] = useLoginMutation();

  React.useEffect(() => {
    if (data) {
      setUser({ jwt: data.jwt });
      window.localStorage.setItem("userJwt", JSON.stringify(data.jwt));
    }
  }, [data]);

  const signin = (
    newUser: {
      phoneNumber: string;
      pin: string;
    },
    callback?: VoidFunction
  ) => {
    return login(newUser).then((res) => {
      if (res && "data" in res) {
        const dark = res.data.darkMode === true;
        localStorage.setItem("color-theme", dark ? "dark" : "light");
        if (dark) {
          document.documentElement.classList.add("dark");
        } else {
          document.documentElement.classList.remove("dark");
        }
      }
      callback && callback();
    });
  };
  const signout = (callback?: VoidFunction) => {
    window.localStorage.removeItem("userJwt");
    setUser(null);
    callback && callback();
  };

  const value = { user, signin, signout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return React.useContext(AuthContext);
}

function RequireAuth({ children }: { children: JSX.Element }) {
  let auth = useAuth();
  let location = useLocation();
  if (!auth.user?.jwt) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}
